import { useMemo } from 'react'

import { useStatements } from '@/modules/projects'
import {
  hasStatementFailed,
  isStatementNotDuplicated,
  isStatementProcessing,
  isStatementStatus,
} from '@/modules/projects/status'
import { failedSegmentPageCount } from './coverage'

const inProgressPhases = [
  { key: 'categorizing', label: 'categorizing' },
  { key: 'extracting', label: 'extracting' },
  { key: 'recognizing', label: 'recognizing' },
  { key: 'segmenting', label: 'segmenting' },
  { key: 'enqueued', label: 'enqueued' },
  { key: 'notStarted', label: 'not started' },
] as const

export type InProgressPhase = (typeof inProgressPhases)[number]

export interface StatementTally {
  statementCount: number
  totalPages: number
  processedPages: number
  processingPages: number
  errorPages: number
  activePhase: InProgressPhase
}

export const useStatementTally = (): StatementTally => {
  const statements = useStatements()

  return useMemo(() => {
    const relevant = statements.filter(isStatementNotDuplicated)

    const pages = relevant.reduce(
      (acc, statement) => {
        acc.totalPages += statement.numberOfPages
        if (isStatementStatus(statement, 'succeeded')) {
          acc.processedPages += statement.numberOfPages
        }
        if (hasStatementFailed(statement)) {
          acc.errorPages += failedSegmentPageCount(statement)
        }
        if (isStatementProcessing(statement)) {
          acc.processingPages += statement.numberOfPages
        }
        return acc
      },
      { totalPages: 0, processedPages: 0, errorPages: 0, processingPages: 0 },
    )

    const activePhase =
      inProgressPhases.find(({ key }) =>
        relevant.some(
          (statement) =>
            isStatementStatus(statement, key) && statement.numberOfPages > 0,
        ),
      ) ?? inProgressPhases[inProgressPhases.length - 1]

    return { ...pages, statementCount: relevant.length, activePhase }
  }, [statements])
}
